import { DateFormatter } from '@repo/toolkit/date'

import { tripDateTitleMapping } from '../../../../components/pages/trip-dates/trip-date.utils'
import { TransportTypeEnum, TripTypeEnum } from '../../enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../enums/trip-bookings/trip-step-type.enum'
import { TripPlaceToCenterRequest } from '../trip-place-to-center-request/trip-place-to-center-request'
import {
  tripBookingLabelDetailsDefaultOptions,
  TripBookingLabelDetailsOptions,
  TripBookingProperties
} from './properties.trip-booking'
import { TripBookingTypes } from './trip-booking'
import { TripBookingAllocationToCenterRequestPartial } from './trip-booking-allocation-to-center-request'
import { TripDate, TripDateLight } from './trip-date'
import { TripStepTypes } from './trip-step'

type TripBookingLabelDetailsType = {
  mainDetails?: string
  secondaryDetails?: string
}

export class TripDateProperties {
  static getCenters(tripDate?: TripDate | TripDateLight): string {
    if (!tripDate || !tripDate.tripPlacesRequests || tripDate.tripPlacesRequests.length === 0)
      return ''

    const result = []

    result.push(tripDate.tripPlacesRequests.map((tp) => tp.center.name))

    return [...new Set(result.flat())].sort().join(', ')
  }

  /**
   * Check if the trip date is a primary trip date
   * @param tripDate
   * @returns boolean
   */
  static isPrimary(tripDate?: TripDate): boolean {
    if (!tripDate) return false
    return tripDate.trip.type === TripTypeEnum.PRIMARY
  }

  /**
   * Get the title of the trip date
   * @param tripDate
   * @returns string | undefined
   */
  static getTitle(tripDate?: TripDate): string | undefined {
    if (!tripDate) return undefined
    return tripDateTitleMapping[tripDate.trip.type]
  }

  /**
   * Get the trip bookings by directionality
   * @param tripDate
   * @returns trip bookings
   */
  static getTripBookingsByDirectionality(
    tripDate?: TripDate
  ): TripBookingTypes.Partial[] | undefined {
    if (!tripDate || tripDate.tripBookings.length === 0) return undefined
    return tripDate.tripBookings.filter(
      (tb) =>
        tb.directionality === TransportTypeEnum.ROUND_TRIP ||
        (tb.directionality === TransportTypeEnum.ONE_WAY &&
          tripDate.directionality === TripStepTypeEnum.INBOUND) ||
        (tb.directionality === TransportTypeEnum.RETURN &&
          tripDate.directionality === TripStepTypeEnum.OUTBOUND)
    )
  }

  /**
   * Get the trip booking details
   * @param tripBooking
   * @param options
   * @returns trip booking details
   */
  static getTripBookingDetails(
    tripBooking?: TripBookingTypes.Partial,
    options: TripBookingLabelDetailsOptions = tripBookingLabelDetailsDefaultOptions,
    directionality?: TripStepTypeEnum
  ): TripBookingLabelDetailsType | undefined {
    if (!tripBooking) return undefined

    // check if we have main reservation
    const mainDetails = TripBookingProperties.getDetails(tripBooking, options, directionality)
    // The secondary reservation details are treated as if they were a main reservation -> same reservation
    const secondaryDetails = TripBookingProperties.getDetails(
      tripBooking.secondaryReservation,
      options,
      directionality
    )

    return { mainDetails, secondaryDetails }
  }

  /**
   * Get the trip steps of a trip date
   * @param tripDate
   * @returns trip steps
   */
  static getTripSteps(tripDate: TripDate): TripStepTypes.Light[] | undefined {
    if (tripDate.tripBookings.length === 0) return undefined

    const { directionality } = tripDate

    const tripSteps: TripStepTypes.Light[] = []

    for (const tripBooking of tripDate.tripBookings) {
      // get the trip step from the inbound or outbound trip step depending on the tripBooking directionality.
      const tripStepsData = TripBookingProperties.getTripStepsByDirectionality(
        directionality,
        tripBooking
      )
      if (tripStepsData && tripStepsData.length > 0) tripSteps.push(...tripStepsData)
    }

    // check if we have any tripSteps
    if (tripSteps.length === 0) return undefined

    return tripSteps
  }

  /**
   * Get the departure date time of the trips
   * @param tripDate
   * @returns departure date time of the trips
   */
  static getTripsDepartureDateTime(tripDate?: TripDate): Date[] | undefined {
    if (!tripDate) return undefined

    const tripSteps = this.getTripSteps(tripDate)

    if (!tripSteps) return undefined

    const sortedTripSteps = TripBookingProperties.sortTripStepsById(tripSteps) ?? []

    return sortedTripSteps.map((tripStep) =>
      DateFormatter.setTime(tripStep.departureDate, tripStep.departureTime)
    )
  }

  /**
   * Sort the allocated places
   * @param allocatedPlaces
   * @returns sorted allocated places
   */
  static sortAllocatedPlaces(
    allocatedPlaces: TripBookingAllocationToCenterRequestPartial[]
  ): TripBookingAllocationToCenterRequestPartial[] {
    return allocatedPlaces.sort((a, b) => {
      const order = {
        [TransportTypeEnum.ONE_WAY]: 0,
        [TransportTypeEnum.RETURN]: 1,
        [TransportTypeEnum.ROUND_TRIP]: 2
      }
      // First sort by directionality order
      const directionalityDiff =
        order[a.tripBooking.directionality] - order[b.tripBooking.directionality]
      // If same directionality, sort by tripBooking id ascending
      return directionalityDiff === 0 ? a.tripBooking.id - b.tripBooking.id : directionalityDiff
    })
  }

  /**
   * Sort the allocated places of the trip place to center requests
   * @param tripPlaceToCenterRequests
   * @returns sorted trip place to center requests
   */
  static sortTripPlaceToCenterRequestsAllocatedPlaces(
    tripPlaceToCenterRequests?: TripPlaceToCenterRequest[]
  ): TripPlaceToCenterRequest[] {
    if (!tripPlaceToCenterRequests) return []

    return tripPlaceToCenterRequests.map((tp) => ({
      ...tp,
      allocatedPlaces: TripDateProperties.sortAllocatedPlaces(tp.allocatedPlaces)
    }))
  }

  /**
   * Get the arrival date time of the trips
   * @param tripDate
   * @returns arrival date time of the trips
   */
  static getTripsArrivalDateTime(tripDate?: TripDate): Date[] | undefined {
    if (!tripDate) return undefined

    const tripSteps = this.getTripSteps(tripDate)
    if (!tripSteps) return undefined

    const sortedTripSteps = TripBookingProperties.sortTripStepsById(tripSteps) ?? []

    return sortedTripSteps.map((tripStep) =>
      DateFormatter.setTime(tripStep.arrivalDate, tripStep.arrivalTime)
    )
  }

  /**
   * Get the HH:MM of the trip step
   * @param tripDate
   * @returns HH:MM of the trip step
   */
  static getTripStepHHMM(tripDate?: TripDate): string | undefined {
    if (!tripDate) return undefined

    // get the trips date time
    const tripsDateTime = TripDateProperties.getTripsDepartureDateTime(tripDate)

    // check if we have any trips date time
    if (!tripsDateTime || tripsDateTime?.length === 0) return undefined

    // return the HH:MM using the first or last trip step in id order
    return DateFormatter.HHmm(
      tripDate.directionality === TripStepTypeEnum.INBOUND
        ? tripsDateTime[0]
        : tripsDateTime[tripsDateTime.length - 1]
    )
  }
}
